import React, { useEffect, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import wsService from '@/services/websocket';

/** Origins of queue rows created by the AI (see app/services/callback_requests.py). */
const AI_CALLBACK_ORIGINS = new Set(['ai_agent', 'ai_detected', 'ai_reschedule_temp']);
const CALL_QUEUE_PATH = '/csr/call-queue';
const MAX_VISIBLE_ALERTS = 3;

interface CallbackAlert {
  queueId: number;
  customerPhone: string;
  customerName?: string | null;
  reason?: string | null;
  origin: string;
}

interface QueueEventData {
  kind?: string;
  queueId?: number;
  customerPhone?: string;
  customerName?: string | null;
  reason?: string | null;
  status?: string;
  origin?: string | null;
}

const wsUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/avatars/ws`;
};

const originLabel = (origin: string): string =>
  origin === 'ai_detected' ? 'AI promised a callback' : 'AI requested a callback';

/**
 * Top-of-screen banner shown to every signed-in user when the AI queues a
 * callback. Listens to the shared websocket for `queue.updated` events that
 * carry an AI origin, and clears an alert once its queue row leaves `pending`
 * or is deleted.
 */
const CallbackAlertBanner: React.FC = () => {
  const [alerts, setAlerts] = useState<CallbackAlert[]>([]);

  useEffect(() => {
    // Keep the socket alive app-wide; connect() is a no-op when already open.
    wsService.connect(wsUrl());

    const handleQueueEvent = (data: QueueEventData) => {
      if (!data || typeof data.queueId !== 'number') return;

      if (data.kind === 'queue.deleted' || (data.kind === 'queue.updated' && data.status && data.status !== 'pending')) {
        setAlerts((prev) => prev.filter((a) => a.queueId !== data.queueId));
        return;
      }

      if (data.kind !== 'queue.updated') return;
      const origin = data.origin || '';
      if (!AI_CALLBACK_ORIGINS.has(origin)) return;

      setAlerts((prev) => {
        if (prev.some((a) => a.queueId === data.queueId)) return prev;
        const next: CallbackAlert = {
          queueId: data.queueId as number,
          customerPhone: data.customerPhone || 'Unknown number',
          customerName: data.customerName,
          reason: data.reason,
          origin,
        };
        return [next, ...prev].slice(0, MAX_VISIBLE_ALERTS);
      });
    };

    wsService.on('call_update', handleQueueEvent);
    return () => wsService.off('call_update', handleQueueEvent);
  }, []);

  if (alerts.length === 0) return null;

  const dismiss = (queueId: number) => setAlerts((prev) => prev.filter((a) => a.queueId !== queueId));

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100 }}>
      {alerts.map((alert) => (
        <Alert
          key={alert.queueId}
          type="warning"
          banner
          showIcon
          icon={<PhoneOutlined />}
          closable
          onClose={() => dismiss(alert.queueId)}
          message={
            <Space wrap>
              <strong>Callback needed:</strong>
              <span>{alert.customerName ? `${alert.customerName} (${alert.customerPhone})` : alert.customerPhone}</span>
              {alert.reason ? <span>— {alert.reason}</span> : null}
              <span style={{ opacity: 0.7 }}>· {originLabel(alert.origin)}</span>
            </Space>
          }
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => {
                dismiss(alert.queueId);
                history.push(CALL_QUEUE_PATH);
              }}
            >
              Open call queue
            </Button>
          }
        />
      ))}
    </div>
  );
};

export default CallbackAlertBanner;
